/**
 * 변수 속성 입력 팝업 (ID / Type / Default / Format)
 *
 * showPopupModal({ children: <VarPropsPopup /> })로 띄운다.
 * CommonModal이 cloneElement로 modalRef와 ref를 주입하므로 forwardRef여야 한다.
 *
 * - 확인 → ref.getSelectedRow() → [{ id, type, default, format }] (행 1건 배열).
 *   SinglePopup과 같은 반환 규약이다. SnCodeBox의 handleReturnFunction이
 *   결과에 .map()을 돌려 code/name을 붙이므로 반드시 배열이어야 한다.
 * - 필수값(ID) 검증은 checkValidity로 한다. showPopupModal에 validation: true를
 *   주면 flag=false일 때 메시지만 띄우고 팝업을 닫지 않는다.
 * - 취소/X는 호출부에서 undefined 또는 "close"로 받는다.
 *
 * cd_popup 연동: PopupSettings에 POPUP_VARPROPS로 등록되어 있어
 * sys_formctrl/sys_formgridcolumn의 cd_popup = 'POPUP_VARPROPS'면 이 팝업이 뜬다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SnComboBox from "@shared/components/SnComboBox";

/** 팝업이 돌려주는 변수 속성 값 */
export interface VarProps {
  id: string;
  /** ID명 — 이 변수가 무엇인지 적어두는 자리 (예: 문서번호) */
  name: string;
  type: string;
  /** 기본값 — 예약어 default와 겹치지 않도록 호출부에서 그대로 쓰면 된다 */
  default: string;
  format: string;
}

interface VarPropsPopupProps {
  /** CommonModal이 주입 (팝업 안에서 직접 확인/닫기 처리할 때 사용) */
  modalRef?: any;
  /** 수정 모드일 때 기존 값 — 없으면 빈 값으로 시작 */
  defaultValue?: Partial<VarProps>;
}

const FIELD_CLASS = "w-full border px-2 py-1 rounded";
const FIELD_STYLE = { borderColor: "rgb(196 196 196)" };

/**
 * Type 선택 목록의 공통코드 분류코드(cd_clas).
 * SnComboBox의 code prop으로 넘기면 getCodeData → mas_codel에서
 * cd_clas = 'SYS205' 이고 yn_use = 'Y' 인 행을 code/name으로 읽어온다.
 * 목록 자체는 공통코드관리(SN_MAS_SET_REG_001)에서 등록/수정한다.
 */
export const TYPE_CD_CLAS = "SYS205";

const VarPropsPopup = forwardRef(
  ({ modalRef, defaultValue }: VarPropsPopupProps, ref) => {
    const idRef = useRef<HTMLInputElement>(null);
    const nameRef = useRef<HTMLInputElement>(null);
    const typeRef = useRef<HTMLInputElement>(null);
    const defRef = useRef<HTMLInputElement>(null);
    const formatRef = useRef<HTMLInputElement>(null);

    const [id, setId] = useState(defaultValue?.id ?? "");
    const [name, setName] = useState(defaultValue?.name ?? "");
    const [type, setType] = useState(defaultValue?.type ?? "");
    const [def, setDef] = useState(defaultValue?.default ?? "");
    const [format, setFormat] = useState(defaultValue?.format ?? "");

    useEffect(() => {
      idRef.current?.focus();
    }, []);

    useImperativeHandle(ref, () => ({
      // validation: true로 열었을 때 확인 직후 호출된다 (false면 안 닫힘)
      checkValidity: () => {
        if (!id.trim()) {
          idRef.current?.focus();
          return { flag: false, message: "ID를 입력해 주세요." };
        }
        return { flag: true, message: "" };
      },
      // SinglePopup과 동일하게 "선택된 행 1건"을 배열로 돌려준다.
      // CommonModal이 배열이면 그대로 onClose하고, SnCodeBox는 여기에 .map()으로
      // code/name을 붙인다(PopupSettings.POPUP_VARPROPS.columns 기준).
      getSelectedRow: () => [
        {
          id: id.trim(),
          name: name.trim(),
          type: type.trim(),
          default: def.trim(),
          format: format.trim(),
        } as VarProps,
      ],
    }));

    // Enter로 다음 칸 이동, 마지막 칸에서는 확인 처리
    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
      next: React.RefObject<HTMLInputElement> | null
    ) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      if (next) next.current?.focus();
      else modalRef?.current?.handleConfirm(true);
    };

    return (
      <div className="w-full">
        <div className="mb-3">
          <label className="block text-sm mb-1">ID</label>
          <input
            ref={idRef}
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, nameRef)}
            className={FIELD_CLASS}
            style={FIELD_STYLE}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm mb-1">ID명</label>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, typeRef)}
            className={FIELD_CLASS}
            style={FIELD_STYLE}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm mb-1">Type</label>
          <SnComboBox
            defaultValue={type}
            code={TYPE_CD_CLAS}
            inputRef={typeRef}
            // 목록을 body로 포탈시킨다(SnComboBox.tsx:206 disablePortal 분기).
            // 기본값은 disablePortal=true라 목록이 팝업 안에 그대로 그려지는데,
            // CommonModal의 DialogContent가 overflow:auto라(CommonModal.tsx:359)
            // height:auto인 이 팝업에서는 목록이 통째로 잘려 선택할 게 안 보인다.
            // SearchPanel/FreeForm이 isSearchPanel/isFormPanel을 넘기는 이유와 같다.
            isFormPanel={true}
            onValueChanged={(v: string) => setType(v)}
            // 선택 후 Enter/Tab이면 "next" → 다음 칸(Default)으로 보낸다
            onFocus={(target: string) => {
              if (target === "next") defRef.current?.focus();
            }}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm mb-1">Default</label>
          <input
            ref={defRef}
            type="text"
            value={def}
            onChange={(e) => setDef(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, formatRef)}
            className={FIELD_CLASS}
            style={FIELD_STYLE}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm mb-1">Format</label>
          <input
            ref={formatRef}
            type="text"
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, null)}
            className={FIELD_CLASS}
            style={FIELD_STYLE}
          />
        </div>
      </div>
    );
  }
);

export default VarPropsPopup;
